
import React, { useState } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { graphqlSchema } from '../../services/graphql.schema';
import { ProfileQL } from '../../models/Profile';
import NumberDisplay from '../components/numberDisplay';

import './Dashboard.summary.scss';

interface DashboardSummaryProps {
    currentUser: ProfileQL;
}

interface CicleStats {
    totalBilled: {
        total: number;
        totalBills: number;
        totalPayments: number;
        totalCreditNotes: number;
    }[];    // [Current month, previous month]
    taxDue: {
        totalTax: number;
        iepsTax: number;
        ivaTax: number;
        isrTax: number;
    }[];    // [Current month, previous month]
}


function DashboardSummary({ currentUser }: DashboardSummaryProps) {
    const [stats, setStats] = useState<CicleStats>(null);

    useQuery(graphqlSchema.PROFILE.cicleStats, {
        onCompleted: ({ cicleStats }: { cicleStats: CicleStats }) => {
            // taxBilled and taxDue have two entries in array, [current month, previous month]
            setStats(cicleStats);
            console.log('setStats: ', cicleStats);
        },
        onError: (e) => console.error('Error loading stats: ', e),
        fetchPolicy: 'cache-and-network'
    });

    const renderStats = () => {
        if (!stats) return null;

        const cur = stats.totalBilled[0];
        const prev = stats.totalBilled[1];
        const curTax = stats.taxDue[0];
        const prevTax = stats.taxDue[1];

        // Returns arrow icon and coloring by comparing current vs previous.
        // positiveIsUp: true  → higher is good (green up, red down)
        // positiveIsUp: false → higher is bad  (red up, green down)
        // Equal → arrow_forward + gray regardless of positiveIsUp
        const billedTrend = (current: number, previous: number, positiveIsUp = true) => {
            const prev = previous ?? 0;
            if (current === prev) {
                return { icon: 'arrow_forward' as const, coloring: 'gray' as const };
            }
            const isUp = current > prev;
            return {
                icon: (isUp ? 'arrow_upward' : 'arrow_downward') as 'arrow_upward' | 'arrow_downward',
                coloring: (positiveIsUp ? (isUp ? 'green' : 'red') : (isUp ? 'red' : 'green')) as 'green' | 'red',
            };
        };

        // Tax details are always gray — higher tax is not a positive or negative outcome
        const taxTrend = (current: number, previous: number) => {
            const prev = previous ?? 0;
            if (current === prev) {
                return { icon: 'arrow_forward' as const, coloring: 'gray' as const };
            }
            return {
                icon: (current > prev ? 'arrow_upward' : 'arrow_downward') as 'arrow_upward' | 'arrow_downward',
                coloring: 'gray' as 'gray',
            };
        };

        const currentBilled = cur.total;
        const billedColor: 'green' | 'orange' = currentBilled >= (prev?.total ?? 0) ? 'green' : 'orange';
        const billedIcon: 'arrow_upward' | 'arrow_downward' = currentBilled >= (prev?.total ?? 0) ? 'arrow_upward' : 'arrow_downward';

        const currentTax = curTax.totalTax;
        const taxIcon: 'arrow_upward' | 'arrow_downward' = currentTax >= (prevTax?.totalTax ?? 0) ? 'arrow_upward' : 'arrow_downward';

        return (
            <>
                <NumberDisplay
                    title="Monto total facturado"
                    value={currentBilled}
                    coloring={billedColor}
                    icon={billedIcon}
                    details={[
                        { label: 'Facturas',        value: cur.totalBills,        ...billedTrend(cur.totalBills,        prev?.totalBills) },
                        { label: 'Comp. de pago',   value: cur.totalPayments,     ...billedTrend(cur.totalPayments,     prev?.totalPayments) },
                        { label: 'Notas de crédito', value: cur.totalCreditNotes, ...billedTrend(cur.totalCreditNotes,  prev?.totalCreditNotes, false) },
                    ]}
                />
                <NumberDisplay
                    title="Impuestos generados"
                    value={currentTax}
                    coloring="gray"
                    icon={taxIcon}
                    details={[
                        { label: 'IVA',  value: curTax.ivaTax,   ...taxTrend(curTax.ivaTax,   prevTax?.ivaTax) },
                        { label: 'IEPS', value: curTax.iepsTax,  ...taxTrend(curTax.iepsTax,  prevTax?.iepsTax) },
                        { label: 'ISR',  value: curTax.isrTax,   ...taxTrend(curTax.isrTax,   prevTax?.isrTax) },
                    ]}
                />
            </>
        );
    }

    return (
        <div className="summaryCards">
            <div className="card mainStatus">
                <h3>
                    ¡Hola {currentUser.fiscalpopProfile.nombre}!
                </h3>
            </div>
            <div>
                <h4>Resumen General</h4>
                <div className="summary">
                    {renderStats()}
                </div>
            </div>
        </div>
    );
}

export default DashboardSummary;
